import { memo, useCallback, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Pencil, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { DataTable } from "@/components/data-table/data-table";
import {
  AdminChangelogEntry,
  useAdminChangelogEntries,
  useDeleteChangelog,
  usePublishChangelog,
} from "@/lib/api/changelog";
import { columns } from "./columns";
import { CreateChangelogDialog } from "./create-changelog-dialog";
import { EditChangelogDialog } from "./edit-changelog-dialog";

interface ActionCellProps {
  entry: AdminChangelogEntry;
  onEdit: (entry: AdminChangelogEntry) => void;
  onDelete: (entry: AdminChangelogEntry) => void;
  onPublish: (entry: AdminChangelogEntry) => void;
}

const ActionCell = memo(function ActionCell({
  entry,
  onEdit,
  onDelete,
  onPublish,
}: ActionCellProps) {
  return (
    <div className="flex items-center gap-1">
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(entry)}
              aria-label="Edit"
            >
              <Pencil className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {!entry.is_published && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onPublish(entry)}
                aria-label="Publish"
              >
                <Send className="size-4 text-primary" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Publish & notify</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(entry)}
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              aria-label="Delete"
            >
              <Trash2 className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
});

export function ChangelogAdminPage() {
  const { data: entries, isLoading } = useAdminChangelogEntries();
  const deleteMutation = useDeleteChangelog();
  const publishMutation = usePublishChangelog();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [editing, setEditing] = useState<AdminChangelogEntry | null>(null);
  const [deleting, setDeleting] = useState<AdminChangelogEntry | null>(null);
  const [publishing, setPublishing] = useState<AdminChangelogEntry | null>(
    null,
  );

  const columnsWithActions = useMemo(
    () => [
      ...columns,
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }: { row: { original: AdminChangelogEntry } }) => (
          <ActionCell
            entry={row.original}
            onEdit={setEditing}
            onDelete={setDeleting}
            onPublish={setPublishing}
          />
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: entries ?? [],
    columns: columnsWithActions,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    state: { sorting, globalFilter },
  });

  const handleConfirmDelete = useCallback(() => {
    if (!deleting) return;
    deleteMutation.mutate(deleting.id);
    setDeleting(null);
  }, [deleting, deleteMutation]);

  const handleConfirmPublish = useCallback(() => {
    if (!publishing) return;
    publishMutation.mutate(publishing.id);
    setPublishing(null);
  }, [publishing, publishMutation]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold leading-7 text-black">Changelog</h2>
        <CreateChangelogDialog />
      </div>

      <div className="mt-6">
        <div className="mb-4">
          <Input
            placeholder="Search updates…"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="max-w-sm"
          />
        </div>
        <DataTable table={table} isPending={isLoading} disablePagination />
      </div>

      <EditChangelogDialog
        entry={editing}
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
      />

      <ConfirmActionDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleConfirmDelete}
        title="Delete changelog entry"
        description={`Delete "${deleting?.title}"? This cannot be undone.`}
        confirmText="Delete"
        loading={deleteMutation.isPending}
      />

      <ConfirmActionDialog
        isOpen={!!publishing}
        onClose={() => setPublishing(null)}
        onConfirm={handleConfirmPublish}
        title="Publish changelog entry"
        description={`Publish "${publishing?.title}"? Targeted users will be notified and the entry becomes visible immediately.`}
        confirmText="Publish"
        confirmVariant="default"
        loading={publishMutation.isPending}
      />
    </div>
  );
}
